import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { Notification } from "@rio-cloud/rio-uikit";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import { UserRepository } from "@amityco/ts-sdk";
import packageBoxImg from "src/assets/png/package-box.png";
import Avatar from "src/components/Avatar/indext";
import { fetchCarrierUsers, assignCarrierManagers } from "src/services/packages";
import { useDebounce } from "src/hooks/useDebounce";
import { ICarrierUser } from "@types";

interface WelcomeFormData {
  selectedUsers: string[];
}

interface Props {
  show: boolean;
  currentStep: number;
  totalSteps: number;
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
}

const StepOne = () => (
  <div className="text-center padding-top-25 padding-bottom-25">
    <span className="text-size-16 display-block">Bem vindo a area de</span>
    <span className="text-size-20 display-block text-bold">Pacotes</span>
    <img src={packageBoxImg} alt="Pacotes" className="margin-top-20 width-90 height-80" />
    <span className="text-size-14 display-block margin-top-20">
      Aqui você administra todos os pacotes enviados e recebidos pela sua transportadora.
      <br />
      Para facilitar o uso, vamos fazer algumas configurações.
    </span>
  </div>
);

const PAGE_SIZE = 9;

interface StepTwoProps {
  users: ICarrierUser[];
  isLoading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedUsers: string[];
  onToggleUser: (userRioId: string) => void;
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onLoadMore: () => void;
  hasMorePages: boolean;
}

const StepTwo = ({
  users,
  isLoading,
  searchValue,
  onSearchChange,
  selectedUsers,
  onToggleUser,
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onLoadMore,
  hasMorePages,
}: StepTwoProps) => (
  <div>
    <div className="text-center margin-bottom-20">
      <span className="display-block" style={{ fontFamily: 'Source Sans Pro', fontWeight: 600, fontSize: 24, lineHeight: '100%' }}>Quem são os responsáveis?</span>
      <span className="display-block margin-top-10" style={{ fontFamily: 'Source Sans Pro', fontWeight: 400, fontSize: 14, lineHeight: '100%' }}>
        Selecione os usuários que irão compor o grupo de Gestores de Pacotes.
      </span>
    </div>
    <div className="display-flex flex-column padding-20">
      <div className="input-group margin-bottom-15">
        <span className="input-group-addon">
          <span className="rioglyph rioglyph-search"></span>
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Buscar usuário"
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>

      <span className="text-size-14 display-block margin-bottom-10 text-left">
        Usuários disponíveis para seleção
      </span>

      {isLoading ? (
        <div className="display-flex flex-column gap-10">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="display-flex align-items-center gap-10 padding-5">
              <div className="placeholder width-20 height-20 rounded" />
              <div className="placeholder width-30 height-30 rounded-circle" />
              <div className="placeholder height-15 rounded" style={{ width: '40%' }} />
              <div className="placeholder height-15 rounded" style={{ width: '30%' }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="width-100pct">
          {users.length === 0 ? (
            <div className="text-center padding-20 text-color-gray">
              Nenhum usuário encontrado
            </div>
          ) : (
            <table className="table table-hover margin-bottom-0">
              <tbody>
              {users.map(user => (
                <tr key={user.userRioId} className="cursor-pointer" onClick={() => onToggleUser(user.userRioId)}>
                  <td style={{ width: 30, verticalAlign: 'middle' }}>
                    <Checkbox
                      checked={selectedUsers.includes(user.userRioId)}
                      onChange={() => onToggleUser(user.userRioId)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <div className="display-flex align-items-center gap-10">
                      <Avatar url={user.photo} width="width-30" />
                      <span className="text-size-14 text-bold">{user.name}</span>
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <div className="display-flex align-items-center gap-5">
                      <span className="rioglyph rioglyph-envelope text-color-gray text-size-16"></span>
                      <span className="text-size-12 text-color-primary">{user.email || "Sem email"}</span>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          )}

          {totalPages > 0 && (
            <div className="display-flex flex-column align-items-center margin-top-15 gap-10">
              <div className="display-flex flex-column align-items-center">
                <div className="display-flex align-items-center gap-10">
                  <button
                    className="btn btn-default btn-xs"
                    onClick={onPrevPage}
                    disabled={currentPage === 0}
                  >
                    <span className="rioglyph rioglyph-chevron-left"></span>
                  </button>
                  <span className="text-size-14">
                    {currentPage + 1}/{totalPages}
                  </span>
                  <button
                    className="btn btn-default btn-xs"
                    onClick={onNextPage}
                    disabled={currentPage >= totalPages - 1}
                  >
                    <span className="rioglyph rioglyph-chevron-right"></span>
                  </button>
                </div>
                <div className="progress width-150 height-2 margin-0 margin-top-5">
                  <div className="progress-bar" style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }} />
                </div>
              </div>
              {hasMorePages && (
                <button
                  className="btn btn-default btn-primary btn-sm margin-top-5"
                  onClick={onLoadMore}
                >
                  Carregar mais
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

const StepThree = () => (
  <div className="text-center padding-top-25 padding-bottom-25">
    <span className="text-size-20 display-block text-bold">Tudo pronto!</span>
    <span
      className="rioglyph rioglyph-ok-sign display-inline-block margin-top-20 text-color-primary text-center"
      style={{ fontSize: 70, width: 70, height: 70, lineHeight: "70px" }}
    ></span>
    <span className="text-size-16 display-block margin-top-20">
      Comece a utilizar a área de Pacotes agora mesmo
    </span>
  </div>
);

const WelcomePackages = ({ show, currentStep, totalSteps, onClose, onBack, onNext, onFinish }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 2 state
  const [allCarrierUsers, setAllCarrierUsers] = useState<ICarrierUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ICarrierUser[]>([]);
  const [isLoadingStepTwo, setIsLoadingStepTwo] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [stepTwoPage, setStepTwoPage] = useState(0);
  const [stepTwoTotalPages, setStepTwoTotalPages] = useState(0);
  const debouncedSearch = useDebounce(searchValue, 400);
  const stepTwoInitialized = useRef(false);

  const [formData, setFormData] = useState<WelcomeFormData>({
    selectedUsers: [],
  });

  const enrichUsersWithAmityPhotos = async (users: ICarrierUser[]): Promise<ICarrierUser[]> => {
    try {
      const userIds = users.map(u => u.userRioId).filter(Boolean);
      if (userIds.length === 0) return users;

      const amityResponse = await UserRepository.getUserByIds(userIds);
      if (!amityResponse?.data) return users;

      const amityMap: Record<string, string> = {};
      amityResponse.data.forEach((amityUser: Amity.User) => {
        const photo = amityUser?.metadata?.imageProfile?.fileUrl;
        if (photo) {
          amityMap[amityUser.userId] = photo;
        }
      });

      return users.map(user => ({
        ...user,
        photo: amityMap[user.userRioId] || user.photo,
      }));
    } catch (error) {
      console.error("Erro ao buscar fotos do Amity:", error);
      return users;
    }
  };

  const paginateUsers = (users: ICarrierUser[], page: number) => {
    const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
    const start = page * PAGE_SIZE;
    const content = users.slice(start, start + PAGE_SIZE);
    setFilteredUsers(content);
    setStepTwoTotalPages(totalPages);
    setStepTwoPage(page);
  };

  const loadCarrierUsers = useCallback(async () => {
    setIsLoadingStepTwo(true);
    try {
      const res = await fetchCarrierUsers();
      const users = res.data.result;
      const enrichedUsers = await enrichUsersWithAmityPhotos(users);
      setAllCarrierUsers(enrichedUsers);
      paginateUsers(enrichedUsers, 0);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setIsLoadingStepTwo(false);
    }
  }, []);

  useEffect(() => {
    if (!show || currentStep !== 2) return;

    if (!stepTwoInitialized.current) {
      stepTwoInitialized.current = true;
      loadCarrierUsers();
    }
  }, [show, currentStep, loadCarrierUsers]);

  useEffect(() => {
    if (!show || currentStep !== 2) return;
    if (allCarrierUsers.length === 0) return;

    const filtered = debouncedSearch
      ? allCarrierUsers.filter(u =>
          u.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (u.email && u.email.toLowerCase().includes(debouncedSearch.toLowerCase()))
        )
      : allCarrierUsers;

    paginateUsers(filtered, 0);
  }, [debouncedSearch, show, currentStep, allCarrierUsers]);

  const handleToggleUser = (userRioId: string) => {
    setFormData(prev => {
      const selected = prev.selectedUsers.includes(userRioId)
        ? prev.selectedUsers.filter(id => id !== userRioId)
        : [...prev.selectedUsers, userRioId];
      return { ...prev, selectedUsers: selected };
    });
  };

  const getCurrentFilteredUsers = () => {
    return debouncedSearch
      ? allCarrierUsers.filter(u =>
          u.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (u.email && u.email.toLowerCase().includes(debouncedSearch.toLowerCase()))
        )
      : allCarrierUsers;
  };

  const handleStepTwoPrevPage = () => {
    if (stepTwoPage > 0) {
      paginateUsers(getCurrentFilteredUsers(), stepTwoPage - 1);
    }
  };

  const handleStepTwoNextPage = () => {
    if (stepTwoPage < stepTwoTotalPages - 1) {
      paginateUsers(getCurrentFilteredUsers(), stepTwoPage + 1);
    }
  };

  const handleStepTwoLoadMore = () => {
    if (stepTwoPage < stepTwoTotalPages - 1) {
      paginateUsers(getCurrentFilteredUsers(), stepTwoPage + 1);
    }
  };

  const handleAssignManagers = async () => {
    if (formData.selectedUsers.length === 0) {
      Notification.warning("Selecione pelo menos um usuário para continuar.");
      return;
    }

    setIsSubmitting(true);
    try {
      await assignCarrierManagers(formData.selectedUsers);
      Notification.success("Gestores atribuídos com sucesso!");
      onNext();
    } catch (error: any) {
      const message = error?.response?.data?.message || "Não foi possível atribuir os gestores.";
      console.error("Erro ao atribuir gestores:", error);
      Notification.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 2) {
      handleAssignManagers();
    } else if (currentStep === totalSteps) {
      onFinish();
    } else {
      onNext();
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepOne />;
      case 2:
        return (
          <StepTwo
            users={filteredUsers}
            isLoading={isLoadingStepTwo}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            selectedUsers={formData.selectedUsers}
            onToggleUser={handleToggleUser}
            currentPage={stepTwoPage}
            totalPages={stepTwoTotalPages}
            onPrevPage={handleStepTwoPrevPage}
            onNextPage={handleStepTwoNextPage}
            onLoadMore={handleStepTwoLoadMore}
            hasMorePages={stepTwoPage < stepTwoTotalPages - 1}
          />
        );
      case 3:
        return <StepThree />;
      default:
        return <StepOne />;
    }
  };

  return (
    <Dialog show={show} onClose={onClose} showCloseButton className="welcome-packages-dialog">
      <Dialog.Title title="" />
      <Dialog.Body>{renderStep()}</Dialog.Body>
      <Dialog.Footer>
        <div className="display-flex justify-content-between align-items-center">
          {currentStep > 1 ? (
            <button
              className="btn btn-default"
              onClick={onBack}
              disabled={isSubmitting}
            >
              <span className="rioglyph rioglyph-chevron-left margin-right-5"></span>Voltar
            </button>
          ) : (
            <div style={{ width: 85 }} />
          )}
          <div className="display-flex flex-column align-items-center">
            <span className="margin-bottom-5">
              {currentStep}/{totalSteps}
            </span>
            <div className="progress width-150 height-2 margin-0">
              <div className="progress-bar" style={{ width: `${progressPercentage}%` }} />
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleNext}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Salvando..."
              : currentStep === totalSteps
                ? "Concluir"
                : "Avançar"}
            {!isSubmitting && (
              <span className="rioglyph rioglyph-chevron-right margin-left-5"></span>
            )}
          </button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
};

export default WelcomePackages;
